// quote_main (docs/conversation-volume-model.md): the per-row reply pill
// quotes into the MAIN thread — the reply lands in the room carrying
// answers.reply_to_answer_id (the aloud edge) instead of in the branch — and
// a branch view becomes the union projection: its native replies plus the
// main-thread quotes of its head, interleaved by created_at.

export type QuoteMainReplyLane = 'quote-main' | 'legacy' | null
export type QuoteRevealResult =
    | 'done'
    | 'fallback'
    | 'not-in-quest'
    | 'not-mounted'
    | 'timeout'
    | 'cancelled'
    | 'superseded'

// Cancellation and supersession reflect a newer user/navigation intent, not
// a missing target. Opening a fallback thread for those outcomes would race
// the newer intent and send the user somewhere they did not choose.
export function shouldFallbackFromQuoteReveal(result?: QuoteRevealResult): boolean {
    return result !== 'done' && result !== 'cancelled' && result !== 'superseded'
}

export interface QuoteNavPanel {
    panelId?: string
    filter?: { questId?: string; answerId?: string } | null
    requestAnswerReveal?: (
        answerId: string,
        options?: Record<string, unknown>,
    ) => Promise<QuoteRevealResult> | QuoteRevealResult
}

// Clicking a quote takes you to the message it quotes, by the same road
// /quest/:questKey/answer/:answerId takes: the panel's filter gains the
// answerId, and the route-owned machinery does the rest — it forces the target
// row into the rendered set, ensures the reveal, applies the deep-link
// highlight, refetches when the row is missing, and leaves the URL on the
// answer. Re-clicking the quote you are already parked on re-reveals directly,
// because the route-owned reveal is idempotent per target and would no-op.
export type QuoteNavPlan =
    | { kind: 'retarget-panel'; panelId: string; answerId: string }
    | { kind: 'reveal-in-place'; answerId: string }
    | { kind: 'open-target-quest'; questId: string; answerId: string }
    | null

export function planQuoteNavigation({
    panel,
    targetAnswerId,
    targetQuestId,
    panelHeadAnswerId,
    panelHeadQuestId,
    targetReplyToAnswerId,
    sourceIsPanelHead = false,
}: {
    panel?: QuoteNavPanel | null
    targetAnswerId?: string | null
    targetQuestId?: string | null
    // The rendered quest's head. A branch's head answer belongs to the PARENT
    // room by quest_id, yet it is the row at the top of this panel — so a
    // guest row quoting it is scrolling within this panel, not leaving it.
    panelHeadAnswerId?: string | null
    panelHeadQuestId?: string | null
    targetReplyToAnswerId?: string | null
    // The quoting row is that head itself (the title). Nothing a title quotes
    // can be a row of its own branch — the head predates every reply below it
    // and every main-thread quote of it — so the trip always leaves this panel
    // for the target's own thread. Until the target's home is known there is
    // nowhere to go: never a retarget, which would park this panel on a row it
    // does not hold.
    sourceIsPanelHead?: boolean
}): QuoteNavPlan {
    const answerId = targetAnswerId ? String(targetAnswerId) : ''
    if (!answerId || !panel) return null

    const panelQuestId = panel.filter?.questId ? String(panel.filter.questId) : ''
    const targetQuest = targetQuestId ? String(targetQuestId) : ''

    if (sourceIsPanelHead) {
        return targetQuest ? { kind: 'open-target-quest', questId: targetQuest, answerId } : null
    }

    const targetIsPanelHead = !!panelHeadAnswerId && String(panelHeadAnswerId) === answerId

    // Imported quotes have a local position despite belonging to the parent
    // room. Match both the source room and the branch head, as the API does.
    const targetIsImportedQuote =
        !!panelHeadAnswerId &&
        !!panelHeadQuestId &&
        targetQuest === String(panelHeadQuestId) &&
        String(targetReplyToAnswerId || '') === String(panelHeadAnswerId)

    if (
        !targetIsPanelHead &&
        !targetIsImportedQuote &&
        targetQuest &&
        panelQuestId &&
        targetQuest !== panelQuestId
    ) {
        return { kind: 'open-target-quest', questId: targetQuest, answerId }
    }

    if (String(panel.filter?.answerId || '') === answerId) {
        return { kind: 'reveal-in-place', answerId }
    }

    return panel.panelId
        ? { kind: 'retarget-panel', panelId: String(panel.panelId), answerId }
        : { kind: 'reveal-in-place', answerId }
}

// A branch's main-thread guest carries a marker that opens the main thread AT
// that reply, to the LEFT — the parent sits left of its branch in the panel
// row. When the panel to the left already shows the main thread (the usual
// case: the reader came from it), retarget that panel rather than inserting a
// second copy beside it.
export type OriginNavPlan =
    | { kind: 'retarget-left'; panelId: string; questId: string; answerId: string }
    | { kind: 'insert-left'; questId: string; answerId: string }
    | null

export function planOriginNavigation({
    panelToLeft,
    originQuestId,
    answerId,
}: {
    panelToLeft?: { panelId?: string; filter?: { questId?: string } | null } | null
    originQuestId?: string | null
    answerId?: string | null
}): OriginNavPlan {
    const questId = originQuestId ? String(originQuestId) : ''
    const answer = answerId ? String(answerId) : ''
    if (!questId || !answer) return null

    const leftQuestId = panelToLeft?.filter?.questId ? String(panelToLeft.filter.questId) : ''
    if (panelToLeft?.panelId && leftQuestId === questId) {
        return {
            kind: 'retarget-left',
            panelId: String(panelToLeft.panelId),
            questId,
            answerId: answer,
        }
    }
    return { kind: 'insert-left', questId, answerId: answer }
}

// How the three-panel window has to move for the trip to be SEEN. The visible
// window is [activeIndex, +1, +2]; the panel left of a left-slot anchor is
// parked one off-screen (display: none), where a reveal only times out.
export type OriginRetargetSlide =
    | { kind: 'slide-to'; index: number }
    | { kind: 'slide-prev' }
    | null

export function planOriginRetargetSlide({
    threePanel,
    targetIndex,
    activeIndex,
}: {
    threePanel: boolean
    targetIndex: number
    activeIndex: number
}): OriginRetargetSlide {
    if (!threePanel) return { kind: 'slide-to', index: targetIndex }
    return targetIndex < activeIndex ? { kind: 'slide-prev' } : null
}

// Inserting left of the RIGHT slot fills that slot with the new panel and pushes
// the anchor off-screen; the window has to advance one so the reader ends on
// [center, main thread, branch]. The animated shift only reads for a center
// anchor, where it plays the same "window advances" motion openCenter uses.
export function planOriginInsertPresentation({
    threePanel,
    anchorIndex,
    centerIndex,
}: {
    threePanel: boolean
    anchorIndex: number
    centerIndex: number
}): { animate: boolean; slideNext: boolean } {
    if (!threePanel) return { animate: false, slideNext: false }
    if (anchorIndex > centerIndex) return { animate: false, slideNext: true }
    return { animate: anchorIndex === centerIndex, slideNext: false }
}

export function resolveQuoteMainReplyLane({
    enabled,
    isTitle,
    isRoomHead = false,
    isQuoteGuest = false,
    answerQuestId,
    renderedQuestId,
    canReplyInMainThread,
    canReplyInLegacyBranch,
}: {
    enabled: boolean
    isTitle: boolean
    isQuoteGuest?: boolean
    // The rendered quest's own head (title): quoting it is valid — the edge
    // names the head even though its quest_id points at the parent room.
    isRoomHead?: boolean
    answerQuestId?: string | number | null
    renderedQuestId?: string | number | null
    canReplyInMainThread: boolean
    canReplyInLegacyBranch: boolean
}): QuoteMainReplyLane {
    const isRoomMessage =
        !isTitle &&
        answerQuestId != null &&
        renderedQuestId != null &&
        String(answerQuestId) === String(renderedQuestId)

    if (
        enabled &&
        (isRoomMessage || isRoomHead || (!isTitle && isQuoteGuest)) &&
        canReplyInMainThread
    )
        return 'quote-main'
    return canReplyInLegacyBranch ? 'legacy' : null
}

// Both ends of an adjacent connector must follow the same rule as quote
// suppression. Guests keep their explicit quote and do not join the chain.
export function shouldConnectAdjacentReply({
    enabled,
    replyToAnswerId,
    prevAnswerId,
    isQuoteGuest = false,
    isEmbed = false,
    isReplyPreview = false,
}: {
    enabled: boolean
    replyToAnswerId?: string | number | null
    prevAnswerId?: string | number | null
    isQuoteGuest?: boolean
    isEmbed?: boolean
    isReplyPreview?: boolean
}): boolean {
    return !!(
        enabled &&
        !isQuoteGuest &&
        !isEmbed &&
        !isReplyPreview &&
        replyToAnswerId &&
        prevAnswerId &&
        String(replyToAnswerId) === String(prevAnswerId)
    )
}

// The inline composer is the next link in the chain when it answers the row
// directly above it. Same adjacency rule the sent replies follow, one row
// later: the quote is dropped from the composer's byline only visually — the
// edge still ships — and the relation is carried by the avatar-gutter line
// running from that last message down into the composer.
//
// The head included. On a thread with no replies it is the row above the
// composer like any other, and the composer draws its own segment — from its
// top edge down to its avatar, inside itself. Nothing is drawn above that edge,
// so the line still never crosses the title.
export function composerConnectorAnswerId({
    enabled,
    quoteAnswerId,
    lastAnswerId,
}: {
    enabled: boolean
    // What the composer is ACTUALLY quoting — after any refusal, so dropping
    // the quote drops the line with it.
    quoteAnswerId?: string | number | null
    lastAnswerId?: string | number | null
}): string | null {
    if (!enabled || !quoteAnswerId || !lastAnswerId) return null
    const quoted = String(quoteAnswerId)
    if (quoted !== String(lastAnswerId)) return null
    return quoted
}

// The upper end of that pair, decided by the row itself. Only the trailing row
// can own it, and only where a composer exists to reach: a stream card, an
// embed or a reply preview renders the same answer with nothing below it.
//
// Never the head. No line crosses the title or the replies bar — a composer
// quoting the head draws its own segment inside itself instead, from its top
// edge down to its avatar.
export function shouldConnectComposerToRow({
    composerQuoteAnswerId,
    answerId,
    isLast,
    isTitle = false,
    isEmbed = false,
    isReplyPreview = false,
}: {
    composerQuoteAnswerId?: string | number | null
    answerId?: string | number | null
    isLast?: boolean
    isTitle?: boolean
    isEmbed?: boolean
    isReplyPreview?: boolean
}): boolean {
    return !!(
        composerQuoteAnswerId &&
        answerId &&
        isLast &&
        !isTitle &&
        !isEmbed &&
        !isReplyPreview &&
        String(composerQuoteAnswerId) === String(answerId)
    )
}

export function quoteMainEnabled(): boolean {
    return true
}
